import { useState, useMemo } from "react";
import { Play, Sparkles, X, Search } from "lucide-react";
import { Input } from "../components/ui/input";
import { videos } from "../mock/mock";
import CTA from "../components/sections/CTA";

const VideosPage = () => {
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => ["All", ...Array.from(new Set(videos.map((v) => v.category)))], []);

  const filtered = useMemo(() => {
    return videos.filter((v) => {
      const q = query.trim().toLowerCase();
      const matchQ = !q || v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q);
      const matchC = activeCategory === "All" || v.category === activeCategory;
      return matchQ && matchC;
    });
  }, [query, activeCategory]);

  return (
    <div>
      <section className="pt-16 pb-12 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Sparkles className="w-4 h-4 text-orange-600" />
            <span className="text-xs font-semibold text-orange-700 tracking-widest uppercase">Video Library</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-stone-800 mb-4">
            Stories in <span className="text-orange-600">Motion</span>
          </h1>
          <p className="text-stone-600 max-w-2xl mx-auto">
            Curated videos on our vision, curriculum, campus, wellness and parent voices — a window into the world of Aadi Gayanam.
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-10">
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
              <Input
                placeholder="Search videos..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 h-11 bg-orange-50 border-orange-200 focus-visible:ring-orange-500"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    activeCategory === c
                      ? "bg-orange-600 text-white border-orange-600"
                      : "bg-white text-stone-700 border-stone-200 hover:border-orange-300"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {/* Video grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((v) => (
              <button
                key={v.id}
                onClick={() => setSelected(v)}
                className="group text-left bg-white rounded-3xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border border-orange-100"
              >
                <div className="relative aspect-video bg-stone-900">
                  <img src={v.thumbnail} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/50 transition-colors">
                    <div className="w-16 h-16 rounded-full bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-7 h-7 text-orange-600 fill-orange-600 ml-1" />
                    </div>
                  </div>
                  <span className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded">
                    {v.duration}
                  </span>
                  <span className="absolute top-3 left-3 bg-white/95 backdrop-blur-sm text-orange-700 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                    {v.category}
                  </span>
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold text-lg text-stone-800 mb-2 line-clamp-2 group-hover:text-orange-700 transition-colors">
                    {v.title}
                  </h3>
                  <p className="text-sm text-stone-600 line-clamp-2">{v.description}</p>
                </div>
              </button>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16">
              <Play className="w-12 h-12 text-orange-300 mx-auto mb-3" />
              <p className="text-stone-500">No videos match your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* Video modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <button
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/40"
            onClick={() => setSelected(null)}
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <div className="aspect-video bg-stone-900 rounded-2xl overflow-hidden shadow-2xl">
              <iframe
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${selected.youtubeId}?autoplay=1&rel=0&modestbranding=1`}
                title={selected.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-5 text-white">
              <span className="text-[10px] font-bold bg-orange-600 px-2.5 py-1 rounded-full uppercase tracking-wider">{selected.category}</span>
              <h3 className="font-display font-bold text-2xl mt-3">{selected.title}</h3>
              <p className="text-white/80 mt-2 max-w-2xl">{selected.description}</p>
            </div>
          </div>
        </div>
      )}

      <CTA />
    </div>
  );
};

export default VideosPage;
